import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Heart, MessageCircle, Share2, MoreVertical, Trash2, MapPin, Calendar, DollarSign,
  Trophy, ChevronLeft, Send, X, Loader2, Camera, Plus, Flag, Pencil, UserPlus, MessageSquare, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ShareContentModal, SharedContentCard } from '../components/SharedContent';
import { buildRecapPayloadFromPost, recapNeedsRebuild } from '../utils/buildRecapPayload';

const API = window.location.origin + '/api';

const REACTIONS = ['🔥', '💰', '👀', '👏', '⛳', '🏆'];

export default function SocialFeed() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showShareContentModal, setShowShareContentModal] = useState(false);
  const [scannedRoundData, setScannedRoundData] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // Check for scanned data from navigation
  useEffect(() => {
    if (location.state?.scanData) {
      setScannedRoundData(location.state.scanData);
      setShowPostModal(true);
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch user
  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/social/feed`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Near-realtime polling: refresh feed every 5s while tab is visible, immediately
  // when the tab becomes visible. Feels instant without WebSocket overhead.
  useEffect(() => {
    let cancelled = false;
    const tick = () => { if (!cancelled && document.visibilityState === 'visible') fetchPosts(); };
    const interval = setInterval(tick, 5000);
    document.addEventListener('visibilitychange', tick);
    return () => { cancelled = true; clearInterval(interval); document.removeEventListener('visibilitychange', tick); };
  }, [fetchPosts]);

  // Auto-open share modal when ?share=1 (deep link from Dashboard/notifications)
  useEffect(() => {
    if (searchParams.get('share') === '1') {
      setShowShareContentModal(true);
    }
  }, [searchParams]);

  // Check for post param — open the post detail, fetching it if not already loaded.
  // Depends only on the postId itself, not on `posts` — depending on `posts` meant
  // this effect re-fired on every 5s poll (a new array reference each time) and
  // reopened the modal even after the user had closed it, since closing it alone
  // doesn't remove `?post=` from the URL.
  const postIdParam = searchParams.get('post');
  useEffect(() => {
    if (!postIdParam) return;
    const local = posts.find(p => p.post_id === postIdParam);
    if (local) {
      setSelectedPost(local);
      return;
    }
    // Not in the loaded page — fetch it directly so older rounds open correctly
    fetch(`${API}/social/feed/${postIdParam}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(p => { if (p && p.post_id) setSelectedPost(p); else toast.error('Could not find that round'); })
      .catch(err => { console.error('post fetch failed', err); toast.error('Could not load that round'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIdParam]);

  // Close the post detail modal and drop `?post=` from the URL so the deep-link
  // effect above doesn't reopen it on the next poll.
  const closePostDetail = useCallback(() => {
    setSelectedPost(null);
    if (searchParams.get('post')) {
      const next = new URLSearchParams(searchParams);
      next.delete('post');
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    }
  }, [searchParams, navigate, location.pathname]);

  const handleLike = async (postId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API}/social/feed/${postId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => 
          p.post_id === postId 
            ? { ...p, likes: data.liked ? [...(p.likes || []), user?.user_id] : (p.likes || []).filter(id => id !== user?.user_id) }
            : p
        ));
      }
    } catch (err) {
      toast.error('Failed to like post');
    }
  };

  const handleReaction = async (postId, reaction) => {
    try {
      const res = await fetch(`${API}/social/feed/${postId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reaction })
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => 
          p.post_id === postId ? { ...p, reactions: data.reactions } : p
        ));
        if (selectedPost?.post_id === postId) {
          setSelectedPost(prev => ({ ...prev, reactions: data.reactions }));
        }
      }
    } catch (err) {
      toast.error('Failed to add reaction');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post? This will also remove all leaderboard entries for this round.')) return;
    try {
      const res = await fetch(`${API}/social/feed/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.filter(p => p.post_id !== postId));
        closePostDetail();
        toast.success(`Post deleted${data.entries_removed ? ` (${data.entries_removed} leaderboard entries removed)` : ''}`);
      }
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleEdit = async (postId, updatedData) => {
    try {
      const res = await fetch(`${API}/social/feed/${postId}/score`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatedData)
      });
      if (res.ok) {
        // Refresh posts to get updated data
        fetchPosts();
        toast.success('Round updated successfully');
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update round');
        return false;
      }
    } catch (err) {
      toast.error('Failed to update round');
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-bundle-green text-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/10 rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-xl font-bold">Bundle Feed</h1>
              <p className="text-xs text-white/70">Rounds, wins & highlights</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowShareContentModal(true)}
              size="sm"
              data-testid="share-content-btn"
              className="bg-white/15 hover:bg-white/25 text-white gap-1 border border-white/20"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Post Link</span>
            </Button>
            <Button
              onClick={() => setShowPostModal(true)}
              size="sm"
              className="bg-bundle-gold hover:bg-bundle-gold/90 text-bundle-green-dark gap-1"
            >
              <Plus className="w-4 h-4" />
              Post
            </Button>
          </div>
        </div>
      </div>

      {/* Feed */}
      <div className="p-4 space-y-4">
        <FeedFilterTabs active={activeFilter} onChange={setActiveFilter} />
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-bundle-green" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-bundle-cream-dark rounded-full flex items-center justify-center mx-auto mb-4">
              <Flag className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-body">No posts yet</p>
            <p className="text-gray-400 text-sm mt-1">Be the first to post a round or share a golf link!</p>
            <div className="mt-4 flex gap-2 justify-center">
              <Button onClick={() => setShowPostModal(true)} className="bg-bundle-green hover:bg-bundle-green/90 text-white">
                Post Round
              </Button>
              <Button onClick={() => setShowShareContentModal(true)} variant="outline">
                Share Link
              </Button>
            </div>
          </div>
        ) : (
          posts
            .filter(post => {
              if (activeFilter === 'all') return true;
              const t = post.post_type;
              const caption = (post.caption || post.message || '').toLowerCase();
              const link = (post.link_url || post.shared_url || '').toLowerCase();
              switch (activeFilter) {
                case 'round':       return t === 'round' || t === 'live_round';
                case 'tournament':  return t === 'tournament' || /tournament|event/.test(caption);
                case 'photos':      return !!(post.image_url || post.photo_url) || (Array.isArray(post.media) && post.media.some(m => (m.type||'').startsWith('image')));
                case 'videos':      return !!post.video_url || /youtube|youtu\.be|vimeo|tiktok|instagram\.com\/(reel|tv)/.test(link) || (Array.isArray(post.media) && post.media.some(m => (m.type||'').startsWith('video')));
                case 'marketplace': return t === 'marketplace' || /marketplace|for sale|selling/.test(caption);
                case 'business':    return t === 'business' || /business|referral|hire/.test(caption);
                case 'travel':      return /travel|golf trip|resort|destination/.test(caption);
                case 'tv':          return t === 'bundle_tv' || /bundle tv|highlight reel/.test(caption);
                case 'ai':          return t === 'ai_post' || post.is_ai_post === true || post.user_name === 'Bundle AI';
                default: return true;
              }
            })
            .map(post => {
            if (post.post_type === 'welcome') {
              return <WelcomeMemberCard key={post.post_id} post={post} onNavigate={navigate} />;
            }
            if (post.post_type === 'shared_content') {
              return (
                <SharedContentCard
                  key={post.post_id}
                  post={post}
                  user={user}
                  onLike={(p) => handleLike(p.post_id)}
                  onOpenComments={(p) => setSelectedPost(p)}
                  onDelete={(p) => handleDelete(p.post_id)}
                />
              );
            }
            return (
              <PostCard
                key={post.post_id}
                post={post}
                user={user}
                onSelect={() => setSelectedPost(post)}
                onLike={(e) => handleLike(post.post_id, e)}
                onReaction={(r) => handleReaction(post.post_id, r)}
                onDelete={() => handleDelete(post.post_id)}
                onEdit={(data) => handleEdit(post.post_id, data)}
              />
            );
          })
        )}
      </div>

      {/* Post Detail Modal */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          user={user}
          onClose={closePostDetail}
          onLike={() => handleLike(selectedPost.post_id)}
          onReaction={(r) => handleReaction(selectedPost.post_id, r)}
          onDelete={() => handleDelete(selectedPost.post_id)}
          onUpdate={(updated) => {
            setPosts(prev => prev.map(p => p.post_id === updated.post_id ? updated : p));
            setSelectedPost(updated);
          }}
        />
      )}

      {/* Post Round Modal */}
      {showPostModal && (
        <PostRoundModal
          onClose={() => {
            setShowPostModal(false);
            setScannedRoundData(null);
          }}
          onSuccess={() => {
            setShowPostModal(false);
            setScannedRoundData(null);
            fetchPosts();
          }}
          initialData={scannedRoundData}
          currentUser={user}
        />
      )}

      {/* Share Golf Content Modal */}
      {showShareContentModal && (
        <ShareContentModal
          onClose={() => setShowShareContentModal(false)}
          onPosted={() => { fetchPosts(); }}
        />
      )}
    </div>
  );
}

// Post Card Component
/**
 * Score summary tailored for OBSERVER posts (poster wasn't on the card).
 * Instead of a giant score attributed to the poster, show the group's low score
 * with the winning player's name so viewers immediately understand it's a group
 * scorecard being reported, not the poster's own round.
 */
function ObserverScoreSummary({ post }) {
  const players = post?.scorecard_data?.players || [];
  // Find lowest-total player
  let low = null;
  players.forEach(p => {
    const t = parseInt(p?.scores?.total, 10);
    if (!Number.isNaN(t) && t > 0 && (low === null || t < low.total)) {
      low = { name: p.name || 'Player', total: t };
    }
  });
  return (
    <div className="flex items-center gap-4 mb-3 flex-wrap" data-testid="observer-score-summary">
      <div className="bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
        <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Group Round</div>
        {low ? (
          <div className="text-sm text-blue-900">
            <span className="font-bold text-lg">{low.total}</span>
            <span className="ml-1 text-blue-800/80">· low by {low.name}</span>
          </div>
        ) : (
          <div className="text-sm text-blue-900 font-medium">{players.length} players</div>
        )}
      </div>
      <div>
        <div className="flex items-center gap-1 text-gray-600">
          <MapPin className="w-4 h-4" />
          <span className="font-medium">{post.course_name || '—'}</span>
        </div>
        <div className="flex items-center gap-1 text-gray-400 text-xs">
          <Calendar className="w-3 h-3" />
          <span>{post.round_date}</span>
          <span className="mx-1">·</span>
          <span className="italic">Observed by {post.user_name}</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Reactions catalog: Facebook-style set with golf-themed additions
// ─────────────────────────────────────────────────────────────────────────
export const CLUB_REACTIONS = [
  { key: 'like',       emoji: '👍', label: 'Like',        color: 'text-blue-500' },
  { key: 'love',       emoji: '❤️', label: 'Love',        color: 'text-red-500' },
  { key: 'great_shot', emoji: '🏌️', label: 'Great Shot',  color: 'text-emerald-600' },
  { key: 'fire',       emoji: '🔥', label: 'Fire',        color: 'text-orange-500' },
  { key: 'laugh',      emoji: '😂', label: 'Laugh',       color: 'text-yellow-500' },
];

function ReactionButton({ post, isLiked, onLike, onReaction }) {
  const [open, setOpen] = React.useState(false);
  const timerRef = React.useRef(null);

  const totalReactions = React.useMemo(() => {
    const r = post?.reactions || {};
    let total = post?.likes?.length || 0;
    Object.values(r).forEach(arr => { if (Array.isArray(arr)) total += arr.length; });
    return total;
  }, [post]);

  const openLater = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), 400);
  };
  const closeLater = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 250);
  };

  return (
    <div className="relative" onMouseLeave={closeLater}>
      <button
        onClick={onLike}
        onMouseEnter={openLater}
        className={`flex items-center gap-2 ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
        data-testid={`post-like-${post.post_id}`}
      >
        <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
        <span className="text-sm">{totalReactions}</span>
      </button>
      {open && (
        <div
          onMouseEnter={() => { if (timerRef.current) clearTimeout(timerRef.current); }}
          className="absolute z-20 -top-11 left-0 bg-white rounded-full shadow-lg border border-gray-200 px-2 py-1.5 flex items-center gap-1 animate-in fade-in slide-in-from-bottom-2 duration-150"
          data-testid={`reaction-picker-${post.post_id}`}
        >
          {CLUB_REACTIONS.map(r => (
            <button
              key={r.key}
              onClick={() => { onReaction && onReaction(r.key); setOpen(false); }}
              title={r.label}
              className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-xl transition-transform hover:scale-125"
              data-testid={`react-${r.key}-${post.post_id}`}
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Feed filter tabs — Facebook-style pill scroller
// ─────────────────────────────────────────────────────────────────────────
export const FEED_FILTERS = [
  { key: 'all',        label: 'All',              emoji: '🏠' },
  { key: 'round',      label: 'Live Rounds',      emoji: '⛳' },
  { key: 'tournament', label: 'Tournaments',      emoji: '🏆' },
  { key: 'photos',     label: 'Photos',           emoji: '📸' },
  { key: 'videos',     label: 'Videos',           emoji: '🎥' },
  { key: 'marketplace',label: 'Marketplace',      emoji: '🛒' },
  { key: 'business',   label: 'Business Network', emoji: '💼' },
  { key: 'travel',     label: 'Travel',           emoji: '✈️' },
  { key: 'tv',         label: 'Bundle TV',        emoji: '📺' },
  { key: 'ai',         label: 'AI Posts',         emoji: '✨' },
];

function FeedFilterTabs({ active, onChange }) {
  return (
    <div className="sticky top-0 z-10 bg-bundle-cream/95 backdrop-blur-sm -mx-4 sm:mx-0 px-4 sm:px-0 py-2 mb-3 border-b border-bundle-cream-dark">
      <div className="flex gap-2 overflow-x-auto scrollbar-hide" data-testid="feed-filter-tabs">
        {FEED_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => onChange(f.key)}
            data-testid={`feed-filter-${f.key}`}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              active === f.key
                ? 'bg-bundle-green text-white shadow-sm'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-bundle-cream-dark'
            }`}
          >
            <span aria-hidden="true">{f.emoji}</span>
            <span>{f.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PostCard({ post, user, onSelect, onLike, onReaction, onDelete, onEdit }) {
  const navigate = useNavigate();
  const isLiked = post.likes?.includes(user?.user_id);
  const isOwner = user?.user_id === post.user_id;
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Inline caption editor (Club Feed / photo / video / gallery posts only —
  // round-post score editing keeps using the existing rich EditModal above).
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState(post.caption || '');
  const [savingCaption, setSavingCaption] = useState(false);
  // "Add more photos" inside the edit modal — same chunked-upload trick as
  // the composer. Lets a poster recover from a partial-upload failure.
  const addMoreInputRef = React.useRef(null);
  const [addingMore, setAddingMore] = useState(false);
  const [addMoreProgress, setAddMoreProgress] = useState(null); // {done,total}
  // Fullscreen lightbox for gallery/photo posts. -1 = closed, else the index
  // of the currently-shown media item in the post's `media` / `media_urls`.
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Compute notable-play stats (birdies, eagles, holes-in-one) for the featured player.
  // We locate the "featured" player as: (1) the row whose total matches post.score, or
  // (2) the row linked to the poster's user_id, or (3) the first player on the card.
  const notableStats = React.useMemo(() => {
    const players = post?.scorecard_data?.players;
    if (!Array.isArray(players) || players.length === 0) return null;
    const rootPars = post?.scorecard_data?.pars || {};
    const courseHoles = post?.scorecard_data?.course_holes || post?.scorecard_data?.holes || [];
    const courseFallback = {};
    if (Array.isArray(courseHoles)) {
      courseHoles.forEach((h, idx) => {
        if (h && h.par) courseFallback[String(idx + 1)] = h.par;
      });
    }
    const pickPars = (p) => {
      const candidates = [p.pars, rootPars, courseFallback];
      for (const c of candidates) {
        if (!c || typeof c !== 'object') continue;
        const norm = {};
        Object.entries(c).forEach(([k, v]) => {
          if (v == null) return;
          const n = parseInt(k, 10);
          const par = parseInt(v, 10);
          if (!Number.isNaN(n) && !Number.isNaN(par)) norm[String(n)] = par;
        });
        if (Object.keys(norm).length) return norm;
      }
      return null;
    };
    // Find featured player
    let featured = players.find(p => p?.scores?.total && parseInt(p.scores.total, 10) === parseInt(post?.score, 10));
    if (!featured) featured = players.find(p => p?.member_id === post?.user_id);
    if (!featured) featured = players[0];
    const pars = pickPars(featured);
    if (!pars) return null;
    let birdies = 0, eagles = 0, holesInOne = 0;
    Object.entries(featured.scores || {}).forEach(([hole, score]) => {
      if (['total', 'OUT', 'IN', 'out', 'in'].includes(hole)) return;
      const holeNum = parseInt(hole, 10);
      if (Number.isNaN(holeNum)) return;
      const par = pars[String(holeNum)];
      const s = parseInt(score, 10);
      if (!par || Number.isNaN(s)) return;
      if (s === 1) holesInOne += 1;
      const diff = s - par;
      if (diff === -1) birdies += 1;
      else if (diff <= -2) eagles += 1;
    });
    if (!birdies && !eagles && !holesInOne) return null;
    return { birdies, eagles, holesInOne };
  }, [post]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleEditSubmit = async (data) => {
    const success = await onEdit(data);
    if (success) {
      setShowEditModal(false);
    }
  };

  return (
    <>
    <div 
      className="bg-white rounded-xl shadow-sm border border-bundle-cream-dark overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={onSelect}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-bundle-cream-dark">
        {post.is_ai_post ? (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center text-white font-black text-xs shadow-inner" data-testid="ai-avatar">
            AI
          </div>
        ) : (
          <img 
            src={post.user_picture || '/default-avatar.png'} 
            alt="" 
            className="w-10 h-10 rounded-full object-cover"
          />
        )}
        <div className="flex-1">
          <p className="font-medium text-gray-900 flex items-center gap-1.5">
            {post.user_name}
            {post.is_ai_post && (
              <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded" data-testid="ai-badge">
                Community Manager
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
        </div>
        {(isOwner || user?.is_admin) && (
          <div className="relative">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <MoreVertical className="w-5 h-5 text-gray-400" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                {post.post_type === 'round' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowEditModal(true); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Scores
                  </button>
                )}
                {post.post_type !== 'round' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setCaptionDraft(post.caption || ''); setEditingCaption(true); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    data-testid={`post-edit-caption-${post.post_id}`}
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Post
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Score highlight — swaps to "Group Round · Low: X by Player" layout for observer posts.
            Hidden entirely for AI posts (no score, no course). */}
        {post.is_ai_post ? null : post.is_observer_post ? (
          <ObserverScoreSummary post={post} />
        ) : (post.post_type === 'round' || post.score) && (
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <div className="bg-bundle-green/10 px-4 py-2 rounded-lg">
              <span className="text-2xl font-bold text-bundle-green">{post.score}</span>
            </div>
            <div>
              <div className="flex items-center gap-1 text-gray-600">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">{post.course_name}</span>
              </div>
              <div className="flex items-center gap-1 text-gray-400 text-sm">
                <Calendar className="w-3 h-3" />
                <span>{post.round_date}</span>
              </div>
            </div>
            {post.total_winnings > 0 && (
              <div className="ml-auto bg-green-100 px-3 py-1 rounded-full flex items-center gap-1">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="font-bold text-green-600">{post.total_winnings.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* Notable-play badge — personal best, birdies, eagles, and holes-in-one for the featured player */}
        {(notableStats || post.is_personal_best) && (
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="notable-play-badges">
            {post.is_personal_best && (
              <span
                data-testid="badge-personal-best"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-orange-100 via-rose-100 to-pink-100 border border-rose-300 text-rose-800 text-xs font-bold shadow-sm animate-pulse-slow"
                title={(() => {
                  const list = post.personal_bests || [];
                  if (!list.length) return 'Personal Best';
                  const p = list[0];
                  return `${p.name}: ${p.new_score} — previous best ${p.previous_best} (−${p.delta})`;
                })()}
              >
                <span aria-hidden="true">🔥</span>
                Personal Best
                {post.personal_bests && post.personal_bests.length > 1 && (
                  <span className="ml-1 opacity-80">×{post.personal_bests.length}</span>
                )}
              </span>
            )}
            {notableStats?.holesInOne > 0 && (
              <span
                data-testid="badge-hole-in-one"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-yellow-100 to-amber-100 border border-amber-300 text-amber-800 text-xs font-bold shadow-sm"
                title="Hole in one!"
              >
                <span aria-hidden="true">🎯</span>
                {notableStats.holesInOne} Hole-in-One
              </span>
            )}
            {notableStats?.eagles > 0 && (
              <span
                data-testid="badge-eagles"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-100 border border-purple-300 text-purple-800 text-xs font-bold shadow-sm"
                title="Eagles"
              >
                <span aria-hidden="true">🦅</span>
                {notableStats.eagles} Eagle{notableStats.eagles !== 1 ? 's' : ''}
              </span>
            )}
            {notableStats?.birdies > 0 && (
              <span
                data-testid="badge-birdies"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold shadow-sm"
                title="Birdies"
              >
                <span aria-hidden="true">🐦</span>
                {notableStats.birdies} Birdie{notableStats.birdies !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Money Breakdown */}
        {(post.player_skins > 0 || post.front9_bundle_value || post.back9_bundle_value) && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3 text-xs">
            <div className="flex flex-wrap gap-3">
              {post.player_skins > 0 && post.skin_value > 0 && (
                <span className="text-green-700">
                  <span className="font-medium">{post.player_skins} skins</span>
                  <span className="text-green-500"> × ${post.skin_value} = ${(post.player_skins * post.skin_value).toFixed(2)}</span>
                </span>
              )}
              {post.front9_bundle_value > 0 && post.front9_winner && (
                <span className="text-green-700">
                  Front 9: <span className="font-medium">${post.front9_bundle_value}</span>
                </span>
              )}
              {post.back9_bundle_value > 0 && post.back9_winner && (
                <span className="text-green-700">
                  Back 9: <span className="font-medium">${post.back9_bundle_value}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Bundle Winners */}
        {(post.front9_winner || post.back9_winner) && (
          <div className="bg-bundle-gold/10 border border-bundle-gold/30 rounded-lg px-3 py-2 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-4 h-4 text-bundle-gold" />
              <span className="text-sm font-medium text-bundle-gold">Bundle Winners</span>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {post.front9_winner && (
                <span className="bg-white/50 px-2 py-1 rounded text-gray-700">
                  Front 9: <span className="font-medium text-bundle-gold">{post.front9_winner}</span>
                  {post.front9_bundle_value > 0 && <span className="text-green-600 ml-1">(${post.front9_bundle_value})</span>}
                  <span className="ml-1">🏆</span>
                </span>
              )}
              {post.back9_winner && (
                <span className="bg-white/50 px-2 py-1 rounded text-gray-700">
                  Back 9: <span className="font-medium text-bundle-gold">{post.back9_winner}</span>
                  {post.back9_bundle_value > 0 && <span className="text-green-600 ml-1">(${post.back9_bundle_value})</span>}
                  <span className="ml-1">🏆</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Teams row — shown when teams data exists */}
        {post.scorecard_data?.teams_data?.enabled && post.scorecard_data.teams_data.teams?.length > 0 && (
          <div className="bg-bundle-cream/40 border border-bundle-cream-dark rounded-lg p-3 mb-3" data-testid="teams-display">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-bundle-green flex items-center gap-1.5">
                <span>👥 Teams</span>
                <span className="text-[10px] font-normal text-gray-500 capitalize">({post.scorecard_data.teams_data.format?.replace('_', ' ')})</span>
              </p>
            </div>
            <div className="space-y-1.5">
              {post.scorecard_data.teams_data.teams.map((team, idx) => {
                const colors = ['bg-blue-500', 'bg-orange-500', 'bg-purple-500', 'bg-pink-500'];
                const isWinner = idx === 0 && team.total_score != null;
                return (
                  <div key={team.team_id} className={`flex items-center gap-2 p-1.5 rounded ${isWinner ? 'bg-bundle-green/10 border border-bundle-green/30' : ''}`}>
                    <span className={`w-6 h-6 rounded-full ${colors[team.team_id % colors.length]} text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0`}>T{team.team_id + 1}</span>
                    <span className="flex-1 text-sm font-semibold text-gray-700 truncate">{team.name}</span>
                    <span className="text-[10px] text-gray-500">F9 {team.front9_score ?? '—'} · B9 {team.back9_score ?? '—'}</span>
                    <span className="text-base font-bold text-bundle-green">{team.total_score ?? '—'}</span>
                    {isWinner && <span className="text-sm">🏆</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Group Recap — full leaderboard from scanned tournament/outing */}
        {post.scorecard_data?.recap_summary?.leaderboard?.length > 0 && (
          <RecapCard recap={post.scorecard_data.recap_summary} teams={post.scorecard_data.teams_data} scorecardData={post.scorecard_data} />
        )}

        {/* Caption — inline editable when the poster taps ⋯ → Edit Post */}
        {editingCaption ? (
          <div className="mb-3" data-testid={`caption-editor-${post.post_id}`}>
            <textarea
              value={captionDraft}
              onChange={(e) => setCaptionDraft(e.target.value)}
              className="w-full border border-bundle-cream-dark rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-bundle-green/40"
              rows={4}
              autoFocus
              placeholder="What's on your mind?"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => addMoreInputRef.current?.click()}
                  disabled={addingMore || (post.media || []).length >= 20}
                  data-testid={`add-more-photos-${post.post_id}`}
                  className="text-xs font-semibold text-bundle-green hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  {addingMore
                    ? `Adding ${addMoreProgress?.done ?? 0}/${addMoreProgress?.total ?? 0}…`
                    : `+ Add photos${(post.media || []).length ? ` (${(post.media || []).length}/20)` : ''}`}
                </button>
                <input
                  ref={addMoreInputRef}
                  type="file"
                  accept="image/*,video/*,.heic,.heif"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const picked = Array.from(e.target.files || []);
                    e.target.value = '';
                    if (picked.length === 0) return;
                    const room = 20 - (post.media || []).length;
                    if (room <= 0) { toast.error('This post already has 20 files.'); return; }
                    const batch = picked.slice(0, room);
                    if (picked.length > room) toast.info(`Adding ${room} — post cap is 20.`);
                    setAddingMore(true);
                    setAddMoreProgress({ done: 0, total: batch.length });
                    let failed = 0;
                    for (let i = 0; i < batch.length; i++) {
                      const f = batch[i];
                      // Same 3-attempt retry pattern the composer uses.
                      let ok = false;
                      for (let attempt = 0; attempt < 3 && !ok; attempt++) {
                        try {
                          const form = new FormData();
                          form.append('media', f);
                          const r = await fetch(`${API}/social/posts/${post.post_id}/media`, {
                            method: 'POST', credentials: 'include', body: form,
                          });
                          if (r.ok) { ok = true; break; }
                          if (r.status >= 400 && r.status < 500) break;
                        } catch (_e) { /* retry */ }
                        if (attempt < 2) await new Promise((res) => setTimeout(res, 800 * (2 ** attempt)));
                      }
                      if (!ok) failed++;
                      setAddMoreProgress({ done: i + 1, total: batch.length });
                    }
                    setAddingMore(false);
                    setAddMoreProgress(null);
                    if (failed > 0) {
                      toast.error(`${batch.length - failed} of ${batch.length} added — ${failed} failed`);
                    } else {
                      toast.success(`Added ${batch.length} file${batch.length !== 1 ? 's' : ''}`);
                    }
                    // Nudge the feed to refetch so the new tiles show up
                    window.dispatchEvent(new CustomEvent('bundle-feed-refresh'));
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setEditingCaption(false); setCaptionDraft(post.caption || ''); }}
                  disabled={savingCaption || addingMore}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold text-gray-500 hover:bg-bundle-cream/60 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid={`caption-save-${post.post_id}`}
                  disabled={savingCaption || addingMore}
                  onClick={async () => {
                    setSavingCaption(true);
                    try {
                      const res = await fetch(`${API}/social/posts/${post.post_id}`, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ caption: captionDraft }),
                      });
                      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Update failed');
                      // Optimistic local update so the caption swaps without a full refetch.
                      post.caption = captionDraft.trim() || null;
                      setEditingCaption(false);
                      if (onEdit) onEdit(post);
                      toast.success('Post updated');
                    } catch (err) {
                      toast.error(err.message || 'Could not update post');
                    } finally {
                      setSavingCaption(false);
                    }
                  }}
                  className="px-4 py-1.5 rounded-full text-xs font-bold text-white bg-bundle-green hover:bg-bundle-green/90 disabled:opacity-50"
                >
                  {savingCaption ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : post.caption ? (
          <p className="text-gray-700 mb-3 whitespace-pre-line">
            {post.caption}
            {post.edited_at && <span className="text-[11px] text-gray-400 ml-1">(edited)</span>}
          </p>
        ) : null}

        {/* AI event reminder — clickable button linking to the event */}
        {post.event_link && post.is_ai_post && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(post.event_link); }}
            data-testid={`ai-event-link-${post.event_id || post.post_id}`}
            className="w-full mb-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-bold text-sm py-2.5 px-4 flex items-center justify-center gap-2 shadow-sm active:scale-[0.99] transition-all"
          >
            <Calendar className="w-4 h-4" />
            <span>View event &amp; RSVP</span>
            <span aria-hidden="true">→</span>
          </button>
        )}

        {/* Media — inline post media_urls[] (Dashboard composer) OR legacy media_path (posts with a stored file on the backend) */}
        {(() => {
          // Prefer the new multi-media list. Fall back to legacy single-file
          // post.media_path so older posts still render.
          const items = Array.isArray(post.media) && post.media.length > 0
            ? post.media
            : (Array.isArray(post.media_urls) && post.media_urls.length > 0
                ? post.media_urls.map((u) => ({ url: u, type: (post.video_url === u ? 'video' : 'photo') }))
                : []);
          if (items.length > 0) {
            const cols = items.length === 1 ? 'grid-cols-1' : items.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
            return (
              <div className={`grid ${cols} gap-1 mb-3 rounded-lg overflow-hidden`} data-testid={`post-media-gallery-${post.post_id}`}>
                {items.map((m, i) => (
                  <button
                    type="button"
                    key={m.url || i}
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                    className={`bg-black/5 block ${items.length === 1 ? '' : 'aspect-square'} focus:outline-none focus:ring-2 focus:ring-bundle-green`}
                    data-testid={`post-media-tile-${post.post_id}-${i}`}
                  >
                    {(m.type === 'video') ? (
                      <div className="relative w-full h-full">
                        <video src={m.url} className="w-full h-full object-cover bg-black" playsInline muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-11 h-11 rounded-full bg-black/60 flex items-center justify-center text-white text-lg">▶</div>
                        </div>
                      </div>
                    ) : (
                      <img src={m.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )}
                  </button>
                ))}
              </div>
            );
          }
          if (post.media_path) {
            return (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(0); }}
                className="block w-full rounded-lg overflow-hidden mb-3"
              >
                {post.media_type?.startsWith('video') ? (
                  <video src={`${API}/social/media/${post.post_id}`} className="w-full" controls />
                ) : (
                  <img src={`${API}/social/media/${post.post_id}`} alt="" className="w-full" />
                )}
              </button>
            );
          }
          return null;
        })()}

        {/* Fullscreen lightbox — appears when a media tile is tapped. Supports
            arrow / swipe navigation across every item on the post. */}
        {lightboxIndex >= 0 && (() => {
          const items = Array.isArray(post.media) && post.media.length > 0
            ? post.media
            : (Array.isArray(post.media_urls) && post.media_urls.length > 0
                ? post.media_urls.map((u) => ({ url: u, type: (post.video_url === u ? 'video' : 'photo') }))
                : post.media_path
                  ? [{ url: `${API}/social/media/${post.post_id}`, type: post.media_type?.startsWith('video') ? 'video' : 'photo' }]
                  : []);
          if (items.length === 0) return null;
          const cur = items[lightboxIndex] || items[0];
          const prev = () => setLightboxIndex((i) => (i - 1 + items.length) % items.length);
          const next = () => setLightboxIndex((i) => (i + 1) % items.length);
          return (
            <MediaLightbox
              cur={cur}
              index={lightboxIndex}
              total={items.length}
              onPrev={prev}
              onNext={next}
              onClose={() => setLightboxIndex(-1)}
            />
          );
        })()}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-6 px-4 py-3 border-t border-bundle-cream-dark">
        <ReactionButton post={post} isLiked={isLiked} onLike={onLike} onReaction={onReaction} />
        <button onClick={onSelect} className="flex items-center gap-2 text-gray-500 hover:text-bundle-green" data-testid={`post-comment-${post.post_id}`}>
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm">{post.comment_count || 0}</span>
        </button>
        <button className="flex items-center gap-2 text-gray-500 hover:text-blue-500 ml-auto">
          <Share2 className="w-5 h-5" />
        </button>
      </div>
    </div>
    
    {/* Edit Round Modal */}
    {showEditModal && (
      <EditRoundModal
        post={post}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditSubmit}
      />
    )}
    </>
  );
}

// Edit Round Modal
function EditRoundModal({ post, onClose, onSave }) {
  const [saving, setSaving] = useState(false);
  const [scorecard, setScorecard] = useState(post.scorecard_data || { players: [], pars: {}, total_par: 72 });
  const [courseName, setCourseName] = useState(post.course_name || '');
  const [roundDate, setRoundDate] = useState(post.round_date || '');

  // Initialize players from scorecard data or create default
  useEffect(() => {
    if (!scorecard.players || scorecard.players.length === 0) {
      setScorecard(prev => ({
        ...prev,
        players: [{ name: post.user_name, scores: { total: post.score } }]
      }));
    }
  }, []);

  // Auto-recalculate skins whenever hole-by-hole scores change.
  // Skins = whoever posts the LOWEST UNIQUE score on a hole wins that hole's skin.
  // Ties = no skin awarded (carry-over not implemented — post-hole basis).
  const recalcSkins = React.useCallback((players) => {
    if (!Array.isArray(players) || players.length < 2) return players;
    const counts = players.map(() => 0);
    for (let h = 1; h <= 18; h++) {
      const key = String(h);
      const holeScores = players.map((p, idx) => {
        const raw = p.scores?.[key];
        const parsed = raw == null || raw === '' ? null : parseInt(raw, 10);
        return { idx, score: Number.isNaN(parsed) ? null : parsed };
      }).filter(x => x.score !== null && x.score > 0);
      if (holeScores.length === 0) continue;
      const minScore = Math.min(...holeScores.map(x => x.score));
      const winners = holeScores.filter(x => x.score === minScore);
      if (winners.length === 1) counts[winners[0].idx] += 1;
    }
    return players.map((p, i) => ({ ...p, skins: counts[i] }));
  }, []);

  const updatePlayerScore = (playerIndex, hole, value) => {
    let newPlayers = [...scorecard.players];
    if (!newPlayers[playerIndex].scores) newPlayers[playerIndex].scores = {};
    newPlayers[playerIndex].scores[String(hole)] = value === '' ? null : parseInt(value);
    
    // Recalculate total from holes
    let total = 0;
    for (let h = 1; h <= 18; h++) {
      const s = parseInt(newPlayers[playerIndex].scores[String(h)]) || 0;
      total += s;
    }
    if (total > 0) newPlayers[playerIndex].scores.total = total;
    
    // Auto-recalc skins for the whole group
    newPlayers = recalcSkins(newPlayers);
    setScorecard(prev => ({ ...prev, players: newPlayers }));
  };

  const updatePlayerTotal = (playerIndex, value) => {
    const newPlayers = [...scorecard.players];
    if (!newPlayers[playerIndex].scores) newPlayers[playerIndex].scores = {};
    newPlayers[playerIndex].scores.total = value === '' ? '' : parseInt(value);
    setScorecard(prev => ({ ...prev, players: newPlayers }));
  };

  const updatePlayerName = (playerIndex, value) => {
    const newPlayers = [...scorecard.players];
    newPlayers[playerIndex].name = value;
    setScorecard(prev => ({ ...prev, players: newPlayers }));
  };

  const updatePlayerSkins = (playerIndex, value) => {
    const newPlayers = [...scorecard.players];
    newPlayers[playerIndex].skins = value === '' ? 0 : parseInt(value);
    setScorecard(prev => ({ ...prev, players: newPlayers }));
  };

  const addPlayer = () => {
    setScorecard(prev => ({
      ...prev,
      players: [...(prev.players || []), { name: 'New Player', scores: {}, skins: 0 }]
    }));
  };

  const removePlayer = (playerIndex) => {
    if (!window.confirm('Remove this player from the round?')) return;
    setScorecard(prev => ({
      ...prev,
      players: prev.players.filter((_, i) => i !== playerIndex)
    }));
  };

  const updatePar = (hole, value) => {
    const newPars = { ...(scorecard.pars || {}) };
    newPars[String(hole)] = value === '' ? null : parseInt(value);
    const totalPar = Object.values(newPars).reduce((s, p) => s + (parseInt(p) || 0), 0);
    setScorecard(prev => ({ ...prev, pars: newPars, total_par: totalPar || prev.total_par }));
  };

  // CTP (Closest To Pin) - stored as {hole: player_name}
  const ctp = scorecard.ctp || {};
  const updateCTP = (hole, playerName) => {
    const newCtp = { ...ctp };
    if (playerName === '' || playerName === null) delete newCtp[hole];
    else newCtp[hole] = playerName;
    setScorecard(prev => ({ ...prev, ctp: newCtp }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ownerName = (post.user_name || '').toLowerCase().trim();
      let ownerIdx = scorecard.players?.findIndex(p => {
        const pn = (p.name || '').toLowerCase().trim();
        if (!pn || !ownerName) return false;
        if (pn === ownerName) return true;
        const oFirst = ownerName.split(' ')[0];
        const oLast = ownerName.split(' ').slice(-1)[0];
        const pFirst = pn.split(' ')[0];
        const pLast = pn.split(' ').slice(-1)[0];
        return (pFirst && pFirst === oFirst) || (oLast && pLast && pLast === oLast);
      }) ?? -1;
      if (ownerIdx < 0) ownerIdx = 0;

      const mainPlayerScore = parseInt(scorecard.players?.[ownerIdx]?.scores?.total) || post.score;

      await onSave({
        score: mainPlayerScore,
        course_name: courseName,
        date: roundDate,
        scorecard_data: { ...scorecard, course_name: courseName }
      });
    } finally {
      setSaving(false);
    }
  };

  // Detect if any player has a hole-by-hole score
  const hasHoleScores = scorecard.players?.some(p =>
    Object.keys(p.scores || {}).some(k => k !== 'total' && k !== 'out' && k !== 'in' && p.scores[k] != null && p.scores[k] !== '')
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden m-2"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-bundle-green text-white">
          <h2 className="font-heading font-bold text-lg">Edit Round</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full" data-testid="edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[80vh]">
          {/* Course & Date */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              <input type="text" value={courseName} onChange={e => setCourseName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bundle-green focus:border-bundle-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input type="date" value={roundDate} onChange={e => setRoundDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bundle-green focus:border-bundle-green" />
            </div>
          </div>

          {/* Par Row Editor */}
          <div className="mb-4 bg-bundle-cream/40 rounded-lg p-3 border border-bundle-cream-dark">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-bundle-green text-sm">Course Pars</h3>
              <span className="text-xs text-gray-500">Total: {scorecard.total_par || Object.values(scorecard.pars || {}).reduce((s, p) => s + (parseInt(p) || 0), 0) || '—'}</span>
            </div>
            <div className="grid grid-cols-9 gap-1 mb-2">
              {[1,2,3,4,5,6,7,8,9].map(hole => (
                <div key={hole} className="text-center">
                  <div className="text-[10px] text-gray-500 font-bold mb-1">H{hole}</div>
                  <input type="number" min="3" max="6" value={scorecard.pars?.[String(hole)] || ''} onChange={e => updatePar(hole, e.target.value)}
                    className="w-full px-1 py-1 border border-gray-200 rounded text-center text-xs" placeholder="—" data-testid={`edit-par-${hole}`} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-9 gap-1">
              {[10,11,12,13,14,15,16,17,18].map(hole => (
                <div key={hole} className="text-center">
                  <div className="text-[10px] text-gray-500 font-bold mb-1">H{hole}</div>
                  <input type="number" min="3" max="6" value={scorecard.pars?.[String(hole)] || ''} onChange={e => updatePar(hole, e.target.value)}
                    className="w-full px-1 py-1 border border-gray-200 rounded text-center text-xs" placeholder="—" data-testid={`edit-par-${hole}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Players Scores */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Players & Hole-by-Hole Scores</h3>
              <button type="button" onClick={addPlayer} className="text-xs font-semibold text-bundle-green hover:text-bundle-green-dark">+ Add Player</button>
            </div>
            
            {scorecard.players?.map((player, pIdx) => (
              <div key={pIdx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <input type="text" value={player.name || ''} onChange={e => updatePlayerName(pIdx, e.target.value)}
                    className="flex-1 px-2 py-1.5 bg-white border border-gray-300 rounded font-semibold text-gray-900 text-sm" placeholder="Player name" />
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-gray-500 font-semibold">SKINS</label>
                    <input type="number" min="0" value={player.skins ?? 0} onChange={e => updatePlayerSkins(pIdx, e.target.value)}
                      className="w-14 px-2 py-1 bg-white border border-gray-300 rounded text-center text-sm font-semibold" data-testid={`edit-skins-${pIdx}`} />
                    <label className="text-[10px] text-gray-500 font-semibold ml-2">TOTAL</label>
                    <input type="number" value={player.scores?.total || ''} onChange={e => updatePlayerTotal(pIdx, e.target.value)}
                      className="w-16 px-2 py-1 bg-white border border-gray-300 rounded text-center font-bold text-base text-bundle-green" data-testid={`edit-total-${pIdx}`} />
                    {scorecard.players.length > 1 && (
                      <button type="button" onClick={() => removePlayer(pIdx)} className="p-1.5 text-gray-400 hover:text-red-500" title="Remove player">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Hole scores — always show */}
                <div className="grid grid-cols-9 gap-1 text-xs">
                  {[1,2,3,4,5,6,7,8,9].map(hole => {
                    const par = scorecard.pars?.[String(hole)];
                    const score = player.scores?.[String(hole)];
                    const diff = score && par ? score - par : null;
                    const cellClass = diff == null ? '' : diff < 0 ? 'bg-red-50 border-red-200' : diff === 0 ? 'bg-emerald-50 border-emerald-200' : '';
                    return (
                      <div key={hole} className="text-center">
                        <div className="text-gray-400 text-[9px] mb-0.5">H{hole} · P{par || '-'}</div>
                        <input type="number" min="1" max="15" value={score || ''} onChange={e => updatePlayerScore(pIdx, hole, e.target.value)}
                          className={`w-full px-1 py-1 border rounded text-center text-sm ${cellClass}`} data-testid={`edit-score-${pIdx}-${hole}`} />
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-9 gap-1 text-xs mt-1.5">
                  {[10,11,12,13,14,15,16,17,18].map(hole => {
                    const par = scorecard.pars?.[String(hole)];
                    const score = player.scores?.[String(hole)];
                    const diff = score && par ? score - par : null;
                    const cellClass = diff == null ? '' : diff < 0 ? 'bg-red-50 border-red-200' : diff === 0 ? 'bg-emerald-50 border-emerald-200' : '';
                    return (
                      <div key={hole} className="text-center">
                        <div className="text-gray-400 text-[9px] mb-0.5">H{hole} · P{par || '-'}</div>
                        <input type="number" min="1" max="15" value={score || ''} onChange={e => updatePlayerScore(pIdx, hole, e.target.value)}
                          className={`w-full px-1 py-1 border rounded text-center text-sm ${cellClass}`} data-testid={`edit-score-${pIdx}-${hole}`} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Closest to Pin */}
          <div className="mt-5 bg-blue-50 rounded-lg p-3 border border-blue-200">
            <h3 className="font-medium text-blue-900 text-sm mb-2 flex items-center gap-2">
              <span>🎯</span> Closest to Pin (CTP)
            </h3>
            <p className="text-[10px] text-blue-700 mb-2">Assign the player who won CTP on each hole. Par-3 holes are highlighted, but you can record CTP for any hole.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18].map(hole => {
                const par = parseInt(scorecard.pars?.[String(hole)]);
                const isPar3 = par === 3;
                return (
                  <div
                    key={hole}
                    className={`bg-white rounded px-2 py-1.5 border ${isPar3 ? 'border-blue-400 ring-1 ring-blue-300' : 'border-blue-200'}`}
                  >
                    <div className="text-[10px] text-blue-700 font-bold mb-0.5">
                      Hole {hole}{isPar3 ? ' · Par 3' : par ? ` · Par ${par}` : ''}
                    </div>
                    <select
                      value={ctp[hole] || ''}
                      onChange={e => updateCTP(hole, e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded px-1 py-1 bg-white"
                      data-testid={`edit-ctp-${hole}`}
                    >
                      <option value="">— none —</option>
                      {scorecard.players?.map((p, idx) => (<option key={idx} value={p.name}>{p.name}</option>))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-bundle-green hover:bg-bundle-green/90 text-white" data-testid="edit-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// Post Detail Modal
function PostedScorecard({ scorecardData }) {
  const players = scorecardData.players || [];
  const pars = scorecardData.pars || (players.find(p => p.pars)?.pars) || {};
  const yardages = scorecardData.yardages || {};
  const numHoles = scorecardData.num_holes || 18;
  const holes = Array.from({ length: numHoles }, (_, i) => i + 1);
  const front9 = holes.slice(0, 9);
  const back9 = holes.slice(9, 18);

  const getScore = (player, h) => {
    if (player.scores) {
      const s = player.scores[String(h)] ?? player.scores[h];
      return (s != null && s !== '') ? s : null;
    }
    if (player.holes) {
      const cell = player.holes[h - 1];
      return cell?.score ?? null;
    }
    return null;
  };
  const sumRange = (player, range) => range.reduce((acc, h) => { const s = getScore(player, h); return acc + (typeof s === 'number' ? s : 0); }, 0);
  const sumPar = (range) => range.reduce((acc, h) => acc + (Number(pars[String(h)] ?? pars[h]) || 0), 0);

  const renderSection = (range, label) => (
    <div className="overflow-x-auto -mx-4 px-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-bundle-green/70 mb-1 ml-1">{label}</div>
      <table className="w-full text-xs border-collapse min-w-[420px]">
        <thead>
          <tr className="bg-bundle-green/10">
            <th className="px-1.5 py-1.5 text-left font-bold text-bundle-green text-[10px] sticky left-0 bg-bundle-green/10">Hole</th>
            {range.map(h => <th key={h} className="px-1.5 py-1.5 text-center text-bundle-green text-[10px] font-bold">{h}</th>)}
            <th className="px-1.5 py-1.5 text-center text-bundle-green text-[10px] font-bold">{label === 'Front 9' ? 'OUT' : label === 'Back 9' ? 'IN' : 'TOT'}</th>
          </tr>
          <tr className="bg-bundle-cream/60 text-[10px] text-gray-500">
            <td className="px-1.5 py-1 font-semibold sticky left-0 bg-bundle-cream/60">Par</td>
            {range.map(h => <td key={h} className="px-1.5 py-1 text-center">{pars[String(h)] ?? pars[h] ?? '—'}</td>)}
            <td className="px-1.5 py-1 text-center font-bold">{sumPar(range) || '—'}</td>
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => {
            const total = sumRange(p, range);
            return (
              <tr key={i} className="border-b border-bundle-cream-dark/40">
                <td className="px-1.5 py-1.5 font-semibold text-bundle-green truncate max-w-[80px] sticky left-0 bg-white">{p.name}</td>
                {range.map(h => {
                  const s = getScore(p, h);
                  const par = Number(pars[String(h)] ?? pars[h]) || null;
                  let cls = 'text-gray-700';
                  if (par != null && typeof s === 'number') {
                    if (s < par - 1) cls = 'text-purple-700 font-bold';        // eagle+
                    else if (s === par - 1) cls = 'text-red-600 font-bold';     // birdie
                    else if (s === par) cls = 'text-gray-700';                  // par
                    else if (s === par + 1) cls = 'text-blue-600';              // bogey
                    else if (s > par + 1) cls = 'text-blue-900 font-bold';      // double+
                  }
                  return <td key={h} className={`px-1.5 py-1.5 text-center ${cls}`}>{s ?? '—'}</td>;
                })}
                <td className="px-1.5 py-1.5 text-center font-bold text-bundle-green">{total || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="mb-4 bg-white border border-bundle-cream-dark rounded-xl p-3" data-testid="posted-scorecard">
      <h4 className="text-xs font-bold text-bundle-green mb-2 flex items-center gap-1.5">
        <Flag className="w-3.5 h-3.5" /> Scorecard
      </h4>
      <div className="space-y-3">
        {renderSection(front9, 'Front 9')}
        {numHoles > 9 && renderSection(back9, 'Back 9')}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[9px] text-gray-400">
        <span className="text-purple-700 font-bold">Eagle</span>
        <span className="text-red-600 font-bold">Birdie</span>
        <span>Par</span>
        <span className="text-blue-600">Bogey</span>
        <span className="text-blue-900 font-bold">Double+</span>
      </div>
    </div>
  );
}

function PostDetailModal({ post, user, onClose, onLike, onReaction, onDelete, onUpdate }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const isLiked = post.likes?.includes(user?.user_id);

  useEffect(() => {
    fetchComments();
  }, [post.post_id]);

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API}/social/feed/${post.post_id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      const res = await fetch(`${API}/social/feed/${post.post_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment })
      });
      if (res.ok) {
        setNewComment('');
        fetchComments();
        onUpdate({ ...post, comment_count: (post.comment_count || 0) + 1 });
      }
    } catch (err) {
      toast.error('Failed to post comment');
    }
  };

  const getReactionCount = (reaction) => post.reactions?.[reaction]?.length || 0;
  const hasReacted = (reaction) => post.reactions?.[reaction]?.includes(user?.user_id);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
      <div className="w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <img src={post.user_picture || '/default-avatar.png'} alt="" className="w-10 h-10 rounded-full" />
            <div>
              <p className="font-medium">{post.user_name}</p>
              <p className="text-xs text-gray-500">{post.round_date}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Score — swaps for observer posts so the poster isn't misrepresented as the shooter */}
          {post.is_observer_post ? (
            <div className="mb-4">
              <ObserverScoreSummary post={post} />
            </div>
          ) : (
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-bundle-green/10 px-6 py-3 rounded-xl">
                <span className="text-3xl font-bold text-bundle-green">{post.score}</span>
              </div>
              <div>
                <p className="font-medium text-lg">{post.course_name}</p>
                {post.winnings > 0 && (
                  <p className="text-bundle-gold font-bold">Won ${post.winnings}</p>
                )}
              </div>
            </div>
          )}

          {post.caption && (
            <p className="text-gray-700 mb-4">{post.caption}</p>
          )}

          {/* Scorecard Table */}
          {post.scorecard_data?.players?.length > 0 && (
            <PostedScorecard scorecardData={post.scorecard_data} />
          )}

          {/* Reactions */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button
              onClick={onLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                isLiked ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              {post.likes?.length || 0}
            </button>
            {REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => onReaction(emoji)}
                className={`px-3 py-2 rounded-full text-lg ${
                  hasReacted(emoji) ? 'bg-bundle-gold/20' : 'bg-gray-100'
                }`}
              >
                {emoji} <span className="text-sm ml-1">{getReactionCount(emoji)}</span>
              </button>
            ))}
          </div>

          {/* Comments */}
          <div className="border-t pt-4">
            <h3 className="font-medium mb-3">Comments ({comments.length})</h3>
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            ) : comments.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">No comments yet</p>
            ) : (
              <div className="space-y-3">
                {comments.map(comment => (
                  <div key={comment.comment_id} className="flex gap-3">
                    <img src={comment.user_picture || '/default-avatar.png'} alt="" className="w-8 h-8 rounded-full" />
                    <div className="flex-1 bg-gray-50 rounded-xl p-3">
                      <p className="font-medium text-sm">{comment.user_name}</p>
                      <p className="text-gray-700 text-sm">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Comment input */}
        <form onSubmit={submitComment} className="p-4 border-t flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:border-bundle-green"
          />
          <button 
            type="submit"
            disabled={!newComment.trim()}
            className="p-2 bg-bundle-green rounded-full text-white disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
}

// Post Round Modal
function PostRoundModal({ onClose, onSuccess, initialData, currentUser }) {
  // Find which player on the scanned card matches the logged-in user (by name, case-insensitive)
  const findMyPlayerIndex = () => {
    if (!initialData?.players?.length || !currentUser?.name) return 0;
    const myName = currentUser.name.toLowerCase().trim();
    const idx = initialData.players.findIndex(p => {
      const pn = (p.name || '').toLowerCase().trim();
      if (!pn) return false;
      // Exact match, or first/last name overlap
      if (pn === myName) return true;
      const myFirst = myName.split(' ')[0];
      const myLast = myName.split(' ').slice(-1)[0];
      const pFirst = pn.split(' ')[0];
      const pLast = pn.split(' ').slice(-1)[0];
      return (pFirst && pFirst === myFirst) || (myLast && pLast && pLast === myLast);
    });
    return idx >= 0 ? idx : 0;
  };

  const myInitialIdx = findMyPlayerIndex();

  // Extract data from scanned scorecard if available
  const getInitialScore = () => {
    if (initialData?.players?.length > 0) {
      // Get the LOGGED-IN USER's score (matched by name), not the first player
      const myPlayer = initialData.players[myInitialIdx];
      return myPlayer?.scores?.total || '';
    }
    return initialData?.score || '';
  };

  // Calculate who won front 9 and back 9 bundles (most skins or lowest score for each side)
  const getInitialBundleWinners = () => {
    if (!initialData?.players || initialData.players.length < 2) {
      return { front9: '', back9: '' };
    }
    
    // Calculate skins for front 9 and back 9 separately
    const front9Skins = {};
    const back9Skins = {};
    initialData.players.forEach((_, idx) => { 
      front9Skins[idx] = 0; 
      back9Skins[idx] = 0;
    });
    
    // Front 9 (holes 1-9)
    for (let hole = 1; hole <= 9; hole++) {
      const scores = initialData.players.map((p, idx) => ({
        idx,
        score: p.scores?.[String(hole)]
      })).filter(s => s.score != null);
      
      if (scores.length > 0) {
        const minScore = Math.min(...scores.map(s => s.score));
        const winners = scores.filter(s => s.score === minScore);
        if (winners.length === 1) {
          front9Skins[winners[0].idx]++;
        }
      }
    }
    
    // Back 9 (holes 10-18)
    for (let hole = 10; hole <= 18; hole++) {
      const scores = initialData.players.map((p, idx) => ({
        idx,
        score: p.scores?.[String(hole)]
      })).filter(s => s.score != null);
      
      if (scores.length > 0) {
        const minScore = Math.min(...scores.map(s => s.score));
        const winners = scores.filter(s => s.score === minScore);
        if (winners.length === 1) {
          back9Skins[winners[0].idx]++;
        }
      }
    }
    
    // Find front 9 winner (most skins, or lowest OUT score)
    let front9Winner = '';
    let maxFront9Skins = 0;
    Object.entries(front9Skins).forEach(([idx, count]) => {
      if (count > maxFront9Skins) {
        maxFront9Skins = count;
        front9Winner = initialData.players[parseInt(idx)].name;
      }
    });
    if (!front9Winner || maxFront9Skins === 0) {
      // Fallback to lowest OUT score
      let lowestOut = Infinity;
      initialData.players.forEach(p => {
        if (p.scores?.out && p.scores.out < lowestOut) {
          lowestOut = p.scores.out;
          front9Winner = p.name;
        }
      });
    }
    
    // Find back 9 winner (most skins, or lowest IN score)
    let back9Winner = '';
    let maxBack9Skins = 0;
    Object.entries(back9Skins).forEach(([idx, count]) => {
      if (count > maxBack9Skins) {
        maxBack9Skins = count;
        back9Winner = initialData.players[parseInt(idx)].name;
      }
    });
    if (!back9Winner || maxBack9Skins === 0) {
      // Fallback to lowest IN score
      let lowestIn = Infinity;
      initialData.players.forEach(p => {
        if (p.scores?.in && p.scores.in < lowestIn) {
          lowestIn = p.scores.in;
          back9Winner = p.name;
        }
      });
    }
    
    return { front9: front9Winner, back9: back9Winner };
  };

  const initialWinners = getInitialBundleWinners();

  const [courseName, setCourseName] = useState(initialData?.course_name || '');
  const [date, setDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);
  const [score, setScore] = useState(getInitialScore());
  const [caption, setCaption] = useState('');
  const [skinValue, setSkinValue] = useState('');
  const [front9BundleValue, setFront9BundleValue] = useState('');
  const [back9BundleValue, setBack9BundleValue] = useState('');
  const [front9Winner, setFront9Winner] = useState(initialWinners.front9);
  const [back9Winner, setBack9Winner] = useState(initialWinners.back9);
  const [addToLeaderboard, setAddToLeaderboard] = useState(true);
  const [posting, setPosting] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(myInitialIdx);
  // Teams: each team has {name, player_indices, format}
  // teams_enabled toggles the whole feature; teams_format is the global default
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [teamsFormat, setTeamsFormat] = useState('best_ball'); // total | best_ball | aggregate
  const [teamAssignments, setTeamAssignments] = useState(() => {
    // Default: auto-pair players (1-2 vs 3-4, 5-6, etc.)
    const arr = [];
    if (initialData?.players) {
      initialData.players.forEach((_, i) => {
        arr[i] = Math.floor(i / 2);
      });
    }
    return arr;
  });

  // Calculate total skins for current player (for winnings calculation)
  const getPlayerSkins = () => {
    if (!initialData?.players) return 0;
    let skins = 0;
    for (let hole = 1; hole <= 18; hole++) {
      const scores = initialData.players.map((p, idx) => ({
        idx,
        score: p.scores?.[String(hole)]
      })).filter(s => s.score != null);
      
      if (scores.length > 0) {
        const minScore = Math.min(...scores.map(s => s.score));
        const winners = scores.filter(s => s.score === minScore);
        if (winners.length === 1 && winners[0].idx === selectedPlayer) {
          skins++;
        }
      }
    }
    return skins;
  };

  const playerSkins = getPlayerSkins();
  const selectedPlayerName = initialData?.players?.[selectedPlayer]?.name || '';

  // Calculate estimated winnings
  const calculateWinnings = () => {
    let total = 0;
    // Skins winnings
    if (skinValue && playerSkins > 0) {
      total += parseFloat(skinValue) * playerSkins;
    }
    // Front 9 bundle
    if (front9BundleValue && front9Winner === selectedPlayerName) {
      total += parseFloat(front9BundleValue);
    }
    // Back 9 bundle
    if (back9BundleValue && back9Winner === selectedPlayerName) {
      total += parseFloat(back9BundleValue);
    }
    return total;
  };

  const estimatedWinnings = calculateWinnings();

  // If we have scanned data with multiple players, show player selector
  const hasMultiplePlayers = initialData?.players?.length > 1;

  // Update score when player selection changes
  useEffect(() => {
    if (initialData?.players?.[selectedPlayer]) {
      const playerScore = initialData.players[selectedPlayer].scores?.total;
      if (playerScore) setScore(playerScore);
    }
  }, [selectedPlayer, initialData]);

  // ============== TEAMS LOGIC ==============
  // Group players by team (returns { teamId: [playerIdx, ...] })
  const groupByTeam = () => {
    const groups = {};
    teamAssignments.forEach((teamId, playerIdx) => {
      if (teamId == null || teamId < 0) return;
      if (!groups[teamId]) groups[teamId] = [];
      groups[teamId].push(playerIdx);
    });
    return groups;
  };

  // Compute team total score given a format
  const computeTeamScore = (playerIndices, format) => {
    if (!initialData?.players || playerIndices.length === 0) return null;
    const players = playerIndices.map(i => initialData.players[i]).filter(Boolean);
    if (players.length === 0) return null;

    if (format === 'total') {
      // Sum of each player's total
      return players.reduce((s, p) => s + (parseInt(p.scores?.total) || 0), 0);
    }
    if (format === 'best_ball') {
      // For each hole, take the lowest score among teammates
      let total = 0;
      for (let h = 1; h <= 18; h++) {
        const scores = players.map(p => parseInt(p.scores?.[String(h)])).filter(s => !isNaN(s) && s > 0);
        if (scores.length > 0) total += Math.min(...scores);
      }
      return total;
    }
    if (format === 'aggregate') {
      // Sum of all players' scores per hole (same as total but per-hole)
      let total = 0;
      for (let h = 1; h <= 18; h++) {
        const scores = players.map(p => parseInt(p.scores?.[String(h)])).filter(s => !isNaN(s) && s > 0);
        total += scores.reduce((a, b) => a + b, 0);
      }
      return total;
    }
    return null;
  };

  // Compute teams summary for submission + display
  const buildTeamsSummary = () => {
    if (!teamsEnabled) return null;
    const groups = groupByTeam();
    const teams = Object.entries(groups).map(([teamId, playerIndices]) => {
      const memberNames = playerIndices.map(i => initialData.players[i]?.name).filter(Boolean);
      return {
        team_id: parseInt(teamId),
        name: memberNames.join(' & ') || `Team ${parseInt(teamId) + 1}`,
        player_indices: playerIndices,
        member_names: memberNames,
        format: teamsFormat,
        total_score: computeTeamScore(playerIndices, teamsFormat),
        front9_score: (() => {
          const players = playerIndices.map(i => initialData.players[i]).filter(Boolean);
          if (teamsFormat === 'best_ball') {
            let total = 0;
            for (let h = 1; h <= 9; h++) {
              const scores = players.map(p => parseInt(p.scores?.[String(h)])).filter(s => !isNaN(s) && s > 0);
              if (scores.length > 0) total += Math.min(...scores);
            }
            return total || null;
          }
          if (teamsFormat === 'aggregate' || teamsFormat === 'total') {
            let total = 0;
            for (let h = 1; h <= 9; h++) {
              const scores = players.map(p => parseInt(p.scores?.[String(h)])).filter(s => !isNaN(s) && s > 0);
              total += scores.reduce((a, b) => a + b, 0);
            }
            return total || null;
          }
          return null;
        })(),
        back9_score: (() => {
          const players = playerIndices.map(i => initialData.players[i]).filter(Boolean);
          if (teamsFormat === 'best_ball') {
            let total = 0;
            for (let h = 10; h <= 18; h++) {
              const scores = players.map(p => parseInt(p.scores?.[String(h)])).filter(s => !isNaN(s) && s > 0);
              if (scores.length > 0) total += Math.min(...scores);
            }
            return total || null;
          }
          if (teamsFormat === 'aggregate' || teamsFormat === 'total') {
            let total = 0;
            for (let h = 10; h <= 18; h++) {
              const scores = players.map(p => parseInt(p.scores?.[String(h)])).filter(s => !isNaN(s) && s > 0);
              total += scores.reduce((a, b) => a + b, 0);
            }
            return total || null;
          }
          return null;
        })(),
      };
    });
    // Sort by total score (lowest = winner first)
    teams.sort((a, b) => (a.total_score ?? Infinity) - (b.total_score ?? Infinity));
    
    // Pick winning teams by side
    const validF9 = teams.filter(t => t.front9_score != null);
    const validB9 = teams.filter(t => t.back9_score != null);
    const winnerF9 = validF9.length > 0 ? [...validF9].sort((a, b) => a.front9_score - b.front9_score)[0] : null;
    const winnerB9 = validB9.length > 0 ? [...validB9].sort((a, b) => a.back9_score - b.back9_score)[0] : null;
    
    return {
      enabled: true,
      format: teamsFormat,
      teams,
      front9_winner_team: winnerF9 ? { team_id: winnerF9.team_id, name: winnerF9.name, score: winnerF9.front9_score } : null,
      back9_winner_team: winnerB9 ? { team_id: winnerB9.team_id, name: winnerB9.name, score: winnerB9.back9_score } : null,
    };
  };
  // ============== END TEAMS ==============

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courseName || !date || !score) {
      toast.error('Please fill in all required fields');
      return;
    }

    setPosting(true);
    try {
      // Include full scorecard data if available
      const scorecardData = initialData?.players ? {
        players: initialData.players,
        selected_player: selectedPlayer,
        overall_confidence: initialData.overall_confidence
      } : null;

      // Calculate all players' skins for the scorecard data
      const allPlayerSkins = {};
      if (initialData?.players) {
        initialData.players.forEach((_, idx) => { allPlayerSkins[idx] = 0; });
        for (let hole = 1; hole <= 18; hole++) {
          const scores = initialData.players.map((p, idx) => ({
            idx,
            score: p.scores?.[String(hole)]
          })).filter(s => s.score != null);
          
          if (scores.length > 0) {
            const minScore = Math.min(...scores.map(s => s.score));
            const winners = scores.filter(s => s.score === minScore);
            if (winners.length === 1) {
              allPlayerSkins[winners[0].idx]++;
            }
          }
        }
      }

      // Build teams summary if enabled
      const teamsData = buildTeamsSummary();
      
      // If teams are enabled, replace bundle winners with team names
      const finalFront9Winner = teamsData?.front9_winner_team?.name || front9Winner || null;
      const finalBack9Winner = teamsData?.back9_winner_team?.name || back9Winner || null;

      // Peer-verification: pull linked Bundle member user_ids from the scorecard's players
      // (populated by the ScorecardScanner when a scorecard row is linked to a member) and
      // send them as tagged_players. Backend enforces "at least one member on the round".
      const taggedPlayers = Array.from(new Set(
        (scorecardData?.players || [])
          .map(p => p.member_id || p.user_id)
          .filter(id => id && id !== currentUser?.user_id)
      ));

      const res = await fetch(`${API}/social/posts/round`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          course_name: courseName,
          date,
          score: parseInt(score),
          caption,
          skin_value: skinValue ? parseFloat(skinValue) : null,
          front9_bundle_value: front9BundleValue ? parseFloat(front9BundleValue) : null,
          back9_bundle_value: back9BundleValue ? parseFloat(back9BundleValue) : null,
          total_winnings: estimatedWinnings > 0 ? estimatedWinnings : null,
          player_skins: playerSkins,
          front9_winner: finalFront9Winner,
          back9_winner: finalBack9Winner,
          tagged_players: taggedPlayers,
          add_to_leaderboard: addToLeaderboard,
          scorecard_data: scorecardData ? { ...scorecardData, all_player_skins: allPlayerSkins, teams_data: teamsData, recap_summary: initialData?.recap_summary || null } : null
        })
      });

      if (res.ok) {
        const result = await res.json();
        
        // Show success with updates
        if (result.updates?.rank_change > 0) {
          toast.success(`Round posted! 🔥 You moved up ${result.updates.rank_change} spot${result.updates.rank_change > 1 ? 's' : ''} on the leaderboard!`);
        } else {
          toast.success('Round added successfully!');
        }
        
        // Show handicap update if changed
        if (result.updates?.handicap_updated && result.updates?.new_handicap !== null) {
          setTimeout(() => {
            toast(`Handicap updated to ${result.updates.new_handicap}`, { icon: '⛳' });
          }, 1500);
        }
        
        // Show new rank
        if (result.updates?.new_rank) {
          setTimeout(() => {
            toast(`You're now ranked #${result.updates.new_rank} on the earnings leaderboard!`, { icon: '🏆' });
          }, 3000);
        }
        
        onSuccess();
      } else {
        const err = await res.json();
        if (err.updates?.error) {
          toast.error(err.updates.error);
        } else {
          toast.error(err.detail || 'Could not post scorecard. Please try again.');
        }
      }
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setPosting(false);
    }
  };

  // Observer mode: the poster is not one of the players on the card
  const isObserverPost = React.useMemo(() => {
    if (!currentUser?.user_id || !initialData?.players?.length) return false;
    return !initialData.players.some(p => p.member_id === currentUser.user_id);
  }, [initialData, currentUser]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h2 className="font-heading font-bold text-lg" data-testid="post-round-modal-title">
            {isObserverPost ? 'Post Scorecard' : 'Post Your Round'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scanned Data Banner */}
        {initialData?.players && (
          <div className="bg-bundle-green/10 border-b border-bundle-green/20 p-3">
            <p className="text-sm text-bundle-green font-medium flex items-center gap-2">
              <span>✅</span> Scorecard scanned successfully
            </p>
            {hasMultiplePlayers && (
              <p className="text-xs text-gray-600 mt-1">Select which player's round to post:</p>
            )}
          </div>
        )}

        {/* Player Selector for scanned data */}
        {hasMultiplePlayers && (
          <div className="p-4 border-b">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Player</label>
            <div className="flex flex-wrap gap-2">
              {initialData.players.map((player, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedPlayer(idx)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedPlayer === idx 
                      ? 'bg-bundle-green text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {player.name} ({player.scores?.total || '?'})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Teams Setup — only when 2+ players */}
        {hasMultiplePlayers && (
          <TeamsBlock
            players={initialData.players}
            teamsEnabled={teamsEnabled}
            setTeamsEnabled={setTeamsEnabled}
            teamsFormat={teamsFormat}
            setTeamsFormat={setTeamsFormat}
            teamAssignments={teamAssignments}
            setTeamAssignments={setTeamAssignments}
            buildTeamsSummary={buildTeamsSummary}
          />
        )}

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
            <input
              type="text"
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              placeholder="Enter course name"
              className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:border-bundle-green"
              required
            />
          </div>

          {/* Observer banner — shown when the poster wasn't on the scorecard */}
          {isObserverPost && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900" data-testid="observer-post-banner">
              <p className="font-semibold">You&apos;re posting on behalf of the group</p>
              <p className="text-xs text-blue-800/80 mt-0.5">This card will show as posted by you, but you aren&apos;t counted as a player. Pick which player&apos;s score should be featured on the feed post below.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:border-bundle-green"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isObserverPost ? 'Featured Score *' : 'Score *'}
              </label>
              <input
                type="number"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                placeholder="72"
                min="50"
                max="200"
                className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:border-bundle-green"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Caption (optional)</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="How was your round?"
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:border-bundle-green resize-none"
            />
          </div>

          {/* Money Section - Skins & Bundles */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Money Game
            </label>
            
            {/* Skins Value */}
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Skin Value (per skin)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  value={skinValue}
                  onChange={(e) => setSkinValue(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.50"
                  className="w-full border border-gray-200 rounded-lg pl-8 pr-4 py-2 text-sm focus:outline-none focus:border-bundle-green"
                />
              </div>
              {playerSkins > 0 && skinValue && (
                <p className="text-xs text-green-600 mt-1">
                  You won {playerSkins} skin{playerSkins !== 1 ? 's' : ''} = ${(parseFloat(skinValue) * playerSkins).toFixed(2)}
                </p>
              )}
            </div>

            {/* Bundle Values */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Front 9 Bundle</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <input
                    type="number"
                    value={front9BundleValue}
                    onChange={(e) => setFront9BundleValue(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="1"
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-bundle-green"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Back 9 Bundle</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <input
                    type="number"
                    value={back9BundleValue}
                    onChange={(e) => setBack9BundleValue(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="1"
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-bundle-green"
                  />
                </div>
              </div>
            </div>

            {/* Estimated Winnings */}
            {estimatedWinnings > 0 && (
              <div className="mt-3 pt-3 border-t border-green-200 flex items-center justify-between">
                <span className="text-sm text-gray-600">Your Estimated Winnings:</span>
                <span className="text-lg font-bold text-green-600">${estimatedWinnings.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Bundle Winners - Front 9 and Back 9 */}
          <div className="bg-bundle-gold/5 border border-bundle-gold/20 rounded-lg p-3">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-bundle-gold" />
              Who Won the Bundle?
            </label>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Front 9 Winner */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Front 9</label>
                {hasMultiplePlayers ? (
                  <select
                    value={front9Winner}
                    onChange={(e) => setFront9Winner(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bundle-green bg-white"
                  >
                    <option value="">Select...</option>
                    {initialData.players.map((player, idx) => (
                      <option key={idx} value={player.name}>
                        {player.name} ({player.scores?.out || '?'})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={front9Winner}
                    onChange={(e) => setFront9Winner(e.target.value)}
                    placeholder="Winner name"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bundle-green"
                  />
                )}
              </div>

              {/* Back 9 Winner */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Back 9</label>
                {hasMultiplePlayers ? (
                  <select
                    value={back9Winner}
                    onChange={(e) => setBack9Winner(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bundle-green bg-white"
                  >
                    <option value="">Select...</option>
                    {initialData.players.map((player, idx) => (
                      <option key={idx} value={player.name}>
                        {player.name} ({player.scores?.in || '?'})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={back9Winner}
                    onChange={(e) => setBack9Winner(e.target.value)}
                    placeholder="Winner name"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-bundle-green"
                  />
                )}
              </div>
            </div>

            {/* Winners summary */}
            {(front9Winner || back9Winner) && (
              <div className="mt-2 pt-2 border-t border-bundle-gold/20 flex flex-wrap gap-2 text-xs">
                {front9Winner && (
                  <span className="bg-bundle-gold/20 text-bundle-gold px-2 py-1 rounded">
                    Front: {front9Winner} 🏆
                  </span>
                )}
                {back9Winner && (
                  <span className="bg-bundle-gold/20 text-bundle-gold px-2 py-1 rounded">
                    Back: {back9Winner} 🏆
                  </span>
                )}
              </div>
            )}
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={addToLeaderboard}
              onChange={(e) => setAddToLeaderboard(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-bundle-green focus:ring-bundle-green"
            />
            <div>
              <p className="font-medium text-gray-700">Add to Leaderboard</p>
              <p className="text-sm text-gray-500">Update your stats and rankings</p>
            </div>
          </label>

          <Button
            type="submit"
            disabled={posting}
            className="w-full bg-bundle-green hover:bg-bundle-green/90 text-white py-3"
          >
            {posting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Post Round'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ============== TEAMS BLOCK COMPONENT ==============
function TeamsBlock({ players, teamsEnabled, setTeamsEnabled, teamsFormat, setTeamsFormat, teamAssignments, setTeamAssignments, buildTeamsSummary }) {
  const teamsSummary = teamsEnabled ? buildTeamsSummary() : null;
  const numTeams = Math.max(2, Math.ceil(players.length / 2));

  const setPlayerTeam = (playerIdx, teamId) => {
    const next = [...teamAssignments];
    next[playerIdx] = teamId;
    setTeamAssignments(next);
  };

  const TEAM_COLORS = [
    { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-700', soft: 'bg-blue-50' },
    { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-700', soft: 'bg-orange-50' },
    { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-700', soft: 'bg-purple-50' },
    { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-700', soft: 'bg-pink-50' },
  ];

  return (
    <div className="p-4 border-b bg-bundle-cream/30">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-bundle-green">Teams (optional)</p>
          <p className="text-xs text-gray-500">A/B vs C/D, pairs for outings, etc.</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" checked={teamsEnabled} onChange={(e) => setTeamsEnabled(e.target.checked)} className="sr-only peer" data-testid="teams-toggle" />
          <div className="w-11 h-6 bg-gray-200 peer-checked:bg-bundle-green rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
        </label>
      </div>

      {teamsEnabled && (
        <div className="space-y-3 mt-3" data-testid="teams-config">
          {/* Format selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Format</label>
            <div className="flex gap-2">
              {[
                { key: 'best_ball', label: 'Best Ball', hint: 'Lowest score per hole counts' },
                { key: 'aggregate', label: 'Aggregate', hint: 'Sum every player per hole' },
                { key: 'total', label: 'Total', hint: "Sum of each player's total" },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setTeamsFormat(opt.key)}
                  title={opt.hint}
                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-bold transition-colors ${teamsFormat === opt.key ? 'bg-bundle-green text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  data-testid={`teams-format-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              {teamsFormat === 'best_ball' && 'Lowest score per hole between teammates counts.'}
              {teamsFormat === 'aggregate' && 'Both teammates\' scores per hole are added together.'}
              {teamsFormat === 'total' && "Each player's total is summed."}
            </p>
          </div>

          {/* Team assignment per player */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Assign players to teams</label>
            <div className="space-y-2">
              {players.map((player, idx) => {
                const currentTeam = teamAssignments[idx];
                return (
                  <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-gray-100" data-testid={`team-assign-row-${idx}`}>
                    <div className="flex-1 text-sm font-medium text-gray-800 truncate">
                      {player.name} <span className="text-gray-400 font-normal">({player.scores?.total || '?'})</span>
                    </div>
                    <div className="flex gap-1">
                      {Array.from({ length: numTeams }, (_, t) => {
                        const c = TEAM_COLORS[t % TEAM_COLORS.length];
                        const active = currentTeam === t;
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setPlayerTeam(idx, t)}
                            className={`w-9 h-9 rounded-full text-xs font-bold transition-all ${active ? `${c.bg} text-white ring-2 ring-offset-1 ring-bundle-green` : `${c.soft} ${c.text} hover:scale-105`}`}
                            data-testid={`team-${t}-player-${idx}`}
                            title={`Team ${t + 1}`}
                          >
                            T{t + 1}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setPlayerTeam(idx, -1)}
                        className={`w-9 h-9 rounded-full text-[10px] font-bold transition-all ${currentTeam === -1 || currentTeam == null ? 'bg-gray-300 text-gray-700 ring-2 ring-offset-1 ring-gray-400' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                        data-testid={`team-none-player-${idx}`}
                        title="Not in a team"
                      >
                        —
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Team standings preview */}
          {teamsSummary && teamsSummary.teams.length > 0 && (
            <div className="bg-white rounded-lg border border-bundle-green/20 p-3" data-testid="teams-preview">
              <p className="text-xs font-bold text-bundle-green mb-2">Team Standings ({teamsSummary.format.replace('_', ' ')})</p>
              <div className="space-y-1.5">
                {teamsSummary.teams.map((team, rank) => {
                  const c = TEAM_COLORS[team.team_id % TEAM_COLORS.length];
                  const isWinner = rank === 0 && team.total_score != null;
                  return (
                    <div key={team.team_id} className={`flex items-center gap-2 p-2 rounded ${isWinner ? 'bg-bundle-green/10 border border-bundle-green/30' : 'bg-gray-50'}`}>
                      <span className={`w-7 h-7 rounded-full ${c.bg} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>T{team.team_id + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{team.name || 'No name'}</p>
                        <p className="text-[10px] text-gray-500">F9: {team.front9_score ?? '—'} · B9: {team.back9_score ?? '—'}</p>
                      </div>
                      <span className="text-lg font-bold text-bundle-green">{team.total_score ?? '—'}</span>
                      {isWinner && <span className="text-base">🏆</span>}
                    </div>
                  );
                })}
              </div>
              {teamsSummary.front9_winner_team && teamsSummary.back9_winner_team && (
                <p className="text-[10px] text-gray-500 mt-2 italic">
                  Bundle winners: F9 → <span className="text-bundle-gold-dark font-semibold">{teamsSummary.front9_winner_team.name}</span> · B9 → <span className="text-bundle-gold-dark font-semibold">{teamsSummary.back9_winner_team.name}</span>
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// ============== END TEAMS BLOCK ==============



export { PostRoundModal };

// ============== GROUP RECAP CARD ==============
function RecapCard({ recap, teams, scorecardData }) {
  const [expanded, setExpanded] = React.useState(false);
  const shareCardRef = React.useRef(null);
  const [sharing, setSharing] = React.useState(false);

  // Prefer explicit low_gross, fall back to legacy low_score for older posts
  const lowGross = recap.low_gross || recap.low_score || null;
  const lowNet = recap.low_net || null;
  const ctpList = Array.isArray(recap.ctp) ? recap.ctp : [];
  const hasTeams = teams?.enabled && Array.isArray(teams.teams) && teams.teams.length > 0;

  const buildRecapText = () => {
    const lines = [
      `⛳ ${recap.course_name} — ${recap.date || ''}`,
      ``,
    ];
    if (lowGross) {
      const svp = lowGross.score_to_par;
      lines.push(`🏆 Low Gross: ${lowGross.name} (${lowGross.total}${svp > 0 ? ', +' + svp : svp === 0 ? ', E' : ', ' + svp})`);
    }
    if (lowNet) {
      lines.push(`🎯 Low Net: ${lowNet.name} (${lowNet.net_total}, HCP ${lowNet.handicap})`);
    }
    if (recap.most_birdies) {
      lines.push(`🐦 Most Birdies: ${recap.most_birdies.name} (${recap.most_birdies.birdies}${recap.most_birdies.eagles > 0 ? ' + ' + recap.most_birdies.eagles + '🦅' : ''})`);
    }
    if (recap.most_skins) {
      lines.push(`💰 Most Skins: ${recap.most_skins.name} (${recap.most_skins.skins})`);
    }
    if (ctpList.length > 0) {
      lines.push(``, `📍 CTP (Closest to Pin):`);
      ctpList.forEach(c => lines.push(`  Hole ${c.hole}: ${c.name}`));
    }
    if (hasTeams) {
      lines.push(``, `👥 Pairings (${(teams.format || '').replace('_', ' ')}):`);
      teams.teams.forEach((t, i) => {
        const score = t.total_score ?? '—';
        lines.push(`${i + 1}. ${t.name} — ${score}`);
      });
    }
    lines.push(``, `LEADERBOARD:`);
    (recap.leaderboard || []).forEach((p, i) => {
      const svp = p.score_to_par > 0 ? `+${p.score_to_par}` : p.score_to_par === 0 ? 'E' : `${p.score_to_par}`;
      const netStr = p.net_total != null ? ` · Net ${p.net_total}` : '';
      lines.push(`${i + 1}. ${p.name} — ${p.total} (${svp})${netStr}`);
    });
    lines.push(``, `Posted from The Bundle Golf Club`);
    return lines.join('\n');
  };

  const handleShareRecap = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const text = buildRecapText();
      // Every existing post predates the new recap fields (pars / skins_by_hole /
      // bundle_wins / leaderboard[].scores) — so if they're missing we rebuild
      // the payload from post.scorecard_data at share time. New posts pass through.
      const payload = recapNeedsRebuild(recap)
        ? (buildRecapPayloadFromPost(scorecardData, recap) || recap)
        : recap;

      let shareUrl = null;
      try {
        const res = await fetch(`${API}/recaps`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = await res.json();
          // Use the OG-tagged endpoint so link previews unfurl inline in
          // iMessage / WhatsApp / Slack. It meta-refreshes to /recap/:shareId.
          shareUrl = window.location.origin + (data.og_url || data.url || `/recap/${data.share_id}`);
        }
      } catch (_e) { /* stay text-only */ }

      const shareText = shareUrl ? `${text}\n\n👉 Full recap: ${shareUrl}` : text;

      // Try sharing an image of the recap card first — WhatsApp / iMessage
      // group chats render an image inline instead of just a link.
      const node = shareCardRef.current;
      if (node && typeof navigator !== 'undefined' && navigator.canShare) {
        try {
          const { default: html2canvas } = await import('html2canvas');
          const canvas = await html2canvas(node, { backgroundColor: '#f5f1e8', scale: 2, useCORS: true });
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
          if (blob) {
            const file = new File([blob], `bundle-recap-${payload.course_name || 'round'}.png`.replace(/\s+/g, '-'), { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              try {
                await navigator.share({ files: [file], title: `${payload.course_name || 'Round'} Recap`, text: shareText, url: shareUrl || undefined });
                return;
              } catch (_e) { /* fall through to text share */ }
            }
          }
        } catch (_e) { /* html2canvas failed — fall through */ }
      }

      if (navigator.share) {
        try {
          await navigator.share({ title: `${recap.course_name} Recap`, text: shareText, url: shareUrl || undefined });
          return;
        } catch { /* fall through */ }
      }
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success(shareUrl ? 'Recap link copied — paste anywhere!' : 'Recap copied to clipboard');
      } catch { toast.error('Could not copy recap'); }
    } finally {
      setSharing(false);
    }
  };

  const visibleLeaders = expanded ? recap.leaderboard : recap.leaderboard.slice(0, 5);

  return (
    <div ref={shareCardRef} className="bg-gradient-to-br from-emerald-50 to-bundle-cream border border-emerald-200 rounded-lg p-3 mb-3" data-testid="post-recap-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-bundle-green flex items-center gap-1.5">
          <span>📊</span> Group Recap
        </p>
        <button
          type="button"
          onClick={handleShareRecap}
          disabled={sharing}
          className="flex items-center gap-1.5 px-2.5 py-1 bg-bundle-green text-white hover:bg-bundle-green-dark rounded-md text-[10px] font-bold transition-colors disabled:opacity-60"
          data-testid="post-recap-share-btn"
        >
          {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
          {sharing ? 'Sharing…' : 'Share Recap'}
        </button>
      </div>
      <div className="space-y-1 mb-2">
        {lowGross && (
          <div className="flex items-center gap-2 text-xs" data-testid="recap-lowgross-row">
            <span>🏆</span><span className="text-gray-500 text-[10px] uppercase font-semibold">Low Gross</span>
            <span className="font-bold text-gray-800 flex-1 truncate">{lowGross.name}</span>
            <span className="font-bold text-bundle-green">{lowGross.total}</span>
          </div>
        )}
        {lowNet && (
          <div className="flex items-center gap-2 text-xs" data-testid="recap-lownet-row">
            <span>🎯</span><span className="text-gray-500 text-[10px] uppercase font-semibold">Low Net</span>
            <span className="font-bold text-gray-800 flex-1 truncate">{lowNet.name}</span>
            <span className="text-[10px] text-gray-500">HCP {lowNet.handicap}</span>
            <span className="font-bold text-cyan-600 ml-1">{lowNet.net_total}</span>
          </div>
        )}
        {recap.most_birdies && (
          <div className="flex items-center gap-2 text-xs">
            <span>🐦</span><span className="text-gray-500 text-[10px] uppercase font-semibold">Birdies</span>
            <span className="font-bold text-gray-800 flex-1 truncate">{recap.most_birdies.name}</span>
            <span className="font-bold text-red-500">{recap.most_birdies.birdies}{recap.most_birdies.eagles > 0 ? ` + ${recap.most_birdies.eagles}🦅` : ''}</span>
          </div>
        )}
        {recap.most_skins && (
          <div className="flex items-center gap-2 text-xs">
            <span>💰</span><span className="text-gray-500 text-[10px] uppercase font-semibold">Skins</span>
            <span className="font-bold text-gray-800 flex-1 truncate">{recap.most_skins.name}</span>
            <span className="font-bold text-bundle-gold">{recap.most_skins.skins}</span>
          </div>
        )}
      </div>

      {ctpList.length > 0 && (
        <div className="border-t border-emerald-200 pt-2 mb-2" data-testid="recap-ctp-block">
          <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
            <span>📍</span> CTP Winners
          </p>
          <div className="flex flex-wrap gap-1">
            {ctpList.map((c, i) => (
              <span key={i} className="text-[10px] bg-pink-100 border border-pink-200 text-pink-700 font-semibold px-2 py-0.5 rounded-full">
                H{c.hole} · {c.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {hasTeams && (
        <div className="border-t border-emerald-200 pt-2 mb-2" data-testid="recap-pairings-block">
          <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
            <span>👥</span> Pairings <span className="font-normal normal-case text-gray-400">({(teams.format || '').replace('_', ' ')})</span>
          </p>
          <div className="space-y-0.5">
            {teams.teams.map((t, i) => (
              <div key={t.team_id ?? i} className={`flex items-center gap-2 text-xs py-0.5 px-1 rounded ${i === 0 ? 'bg-bundle-green/10' : ''}`}>
                <span className={`w-4 text-center font-bold ${i === 0 ? 'text-bundle-gold' : 'text-gray-500'}`}>{i + 1}</span>
                <span className="flex-1 truncate text-gray-800 font-semibold">{t.name}</span>
                <span className="text-[10px] text-gray-500">F9 {t.front9_score ?? '—'} · B9 {t.back9_score ?? '—'}</span>
                <span className="font-bold text-bundle-green w-8 text-right">{t.total_score ?? '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-emerald-200 pt-2">
        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Leaderboard ({recap.leaderboard.length})</p>
        <div className="space-y-0.5">
          {visibleLeaders.map((p, i) => {
            const svp = p.score_to_par;
            return (
              <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                <span className={`w-4 text-center font-bold ${i === 0 ? 'text-bundle-gold' : i === 1 ? 'text-gray-500' : i === 2 ? 'text-amber-700' : 'text-gray-400'}`}>{i + 1}</span>
                <span className="flex-1 truncate text-gray-800">{p.name}</span>
                <span className="font-bold text-bundle-green">{p.total}</span>
                {p.net_total != null && (
                  <span className="text-[10px] text-cyan-600 font-semibold w-10 text-right" title={`Net (HCP ${p.handicap})`}>N{p.net_total}</span>
                )}
                <span className={`text-[10px] w-9 text-right font-semibold ${svp < 0 ? 'text-red-500' : svp === 0 ? 'text-bundle-green' : 'text-gray-500'}`}>{svp > 0 ? `+${svp}` : svp === 0 ? 'E' : svp}</span>
              </div>
            );
          })}
        </div>
        {recap.leaderboard.length > 5 && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full mt-2 text-[11px] text-bundle-green hover:text-bundle-green-dark font-semibold"
            data-testid="post-recap-toggle"
          >
            {expanded ? '▲ Show less' : `▼ Show all ${recap.leaderboard.length}`}
          </button>
        )}
      </div>
    </div>
  );
}
// ============== END RECAP CARD ==============


// ============== WELCOME MEMBER CARD (24h auto-expiring) ==============
function WelcomeMemberCard({ post, onNavigate }) {
  const w = post.welcome_data || {};
  const displayName = w.display_name || post.user_name || 'New Golfer';
  const initials = displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const headline = w.headline || `🔥 New golfer joined from ${w.location || 'the club'}`;
  const [showInviteMenu, setShowInviteMenu] = React.useState(false);

  const goToProfile = () => {
    onNavigate(`/members?user=${post.user_id}`);
  };

  const sendDM = () => {
    onNavigate('/chat', {
      state: {
        startDmWith: { user_id: post.user_id, name: displayName },
        prefillMessage: `Hey ${displayName.split(' ')[0]}! Welcome to The Bundle. Want to play soon?`,
      }
    });
  };

  const postTeeTime = () => {
    onNavigate('/golfers-near-me', {
      state: {
        openPostForm: true,
        prefillDescription: `Welcome ${displayName} from ${w.location || 'the club'}! Come join us — open spots in our group.`,
      }
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const hours = Math.floor((now - date) / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 via-white to-bundle-cream border border-emerald-200 rounded-xl shadow-sm overflow-hidden"
      data-testid={`welcome-card-${post.user_id}`}
    >
      {/* Welcome banner */}
      <div className="bg-bundle-green text-white px-4 py-2 flex items-center gap-2">
        <UserPlus className="w-4 h-4" />
        <span className="text-xs font-bold uppercase tracking-wider">New Member Welcome</span>
        <span className="ml-auto text-[10px] opacity-80">{formatDate(post.created_at)}</span>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          {post.user_picture ? (
            <img
              src={post.user_picture}
              alt=""
              className="w-12 h-12 rounded-full object-cover border-2 border-bundle-green/30"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-bundle-green text-white font-heading font-bold flex items-center justify-center border-2 border-bundle-green/30">
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-bundle-green text-base leading-tight">
              {headline}
            </p>
            <p className="text-sm text-gray-600 mt-0.5">
              <span className="font-semibold">{displayName}</span>
              {w.location && <span className="text-gray-500"> · {w.location}</span>}
            </p>
            {w.home_course && (
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Home Course: <span className="font-medium text-gray-700">{w.home_course}</span>
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <button
            type="button"
            onClick={goToProfile}
            data-testid="welcome-view-profile-btn"
            className="flex flex-col items-center gap-1 py-2.5 px-1 bg-white hover:bg-bundle-green/5 border border-bundle-cream-dark hover:border-bundle-green/40 rounded-lg transition-colors"
          >
            <Users className="w-4 h-4 text-bundle-green" />
            <span className="text-[11px] font-semibold text-gray-700">View Profile</span>
          </button>
          <button
            type="button"
            onClick={goToProfile}
            data-testid="welcome-connect-btn"
            className="flex flex-col items-center gap-1 py-2.5 px-1 bg-bundle-green text-white hover:bg-bundle-green/90 rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span className="text-[11px] font-semibold">Connect</span>
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowInviteMenu(s => !s)}
              data-testid="welcome-invite-btn"
              className="w-full flex flex-col items-center gap-1 py-2.5 px-1 bg-bundle-gold text-white hover:bg-bundle-gold/90 rounded-lg transition-colors"
            >
              <Flag className="w-4 h-4" />
              <span className="text-[11px] font-semibold">Invite to Game</span>
            </button>
            {showInviteMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowInviteMenu(false)} />
                <div className="absolute right-0 bottom-full mb-2 z-20 w-52 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setShowInviteMenu(false); sendDM(); }}
                    data-testid="welcome-invite-dm-btn"
                    className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-bundle-green/5 flex items-center gap-2 border-b border-gray-100"
                  >
                    <MessageSquare className="w-4 h-4 text-bundle-green" />
                    <div>
                      <p className="font-semibold">Send DM</p>
                      <p className="text-[10px] text-gray-500">Quick private hello</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowInviteMenu(false); postTeeTime(); }}
                    data-testid="welcome-invite-tee-btn"
                    className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-bundle-green/5 flex items-center gap-2"
                  >
                    <Flag className="w-4 h-4 text-bundle-gold" />
                    <div>
                      <p className="font-semibold">Post Tee Time</p>
                      <p className="text-[10px] text-gray-500">Public open invite</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
// ============== END WELCOME MEMBER CARD ==============


// ============== MEDIA LIGHTBOX ==============
// Fullscreen viewer for post galleries. Swipe / arrow-key / on-screen buttons
// navigate; tap the backdrop or the X to close.
function MediaLightbox({ cur, index, total, onPrev, onNext, onClose }) {
  const touchRef = React.useRef({ x: 0, y: 0 });
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && total > 1) onPrev();
      else if (e.key === 'ArrowRight' && total > 1) onNext();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, onPrev, onNext, total]);
  const onTouchStart = (e) => {
    const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0 && total > 1) onPrev(); else if (total > 1) onNext();
    }
  };
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="media-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        ✕
      </button>
      {total > 1 && (
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-semibold">
          {index + 1} / {total}
        </div>
      )}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center text-2xl"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center text-2xl"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            ›
          </button>
        </>
      )}
      <div className="max-w-full max-h-full p-4" onClick={(e) => e.stopPropagation()}>
        {cur.type === 'video' ? (
          <video
            src={cur.url}
            controls
            autoPlay
            playsInline
            className="max-w-[95vw] max-h-[90vh] bg-black"
          />
        ) : (
          <img
            src={cur.url}
            alt=""
            className="max-w-[95vw] max-h-[90vh] object-contain"
          />
        )}
      </div>
    </div>
  );
}
// ============== END MEDIA LIGHTBOX ==============
